import React from "react";
import { UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  FileUpload,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Separator,
} from "@/components";
import { Combobox } from "@/components/ui/combobox";
import { TUploadValue } from "@/components/ui/file-upload";
import { FloatingLabelInput } from "@/components/ui/floating-label-input";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import { TClientPreRegisterFormData } from "../../clients.schema";
import { TClient } from "../../clients.types";

export interface IClientPreRegisterFormProps {
  selectedClient: TClient | null;
  form: UseFormReturn<TClientPreRegisterFormData>;
  onSubmit: (values: TClientPreRegisterFormData) => void;
  setIsClientFormOpen: (isClientFormOpen: boolean) => void;
  /** Fecha o formulário e limpa estado de edição (parceiro selecionado). */
  onClose?: () => void;
  handleAddressBlur?: (event: React.FocusEvent<HTMLInputElement>) => Promise<void>;
}

export const ClientPreRegisterForm: React.FC<IClientPreRegisterFormProps> = ({
  form,
  onSubmit,
  setIsClientFormOpen,
  onClose,
  handleAddressBlur,
  selectedClient,
}) => {
  const { t } = useI18n();

  const businessClassificationOptions = [
    { value: "comercio", label: t("clients.business_classification.comercial") },
    { value: "industria", label: t("clients.business_classification.industria") },
    { value: "servicos", label: t("clients.business_classification.servicos") },
  ];

  return (
    <Card className="p-4">
      <div className="mb-6 space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          {selectedClient
            ? t("clients.form.edit.title")
            : t("clients.form.create.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {selectedClient
            ? t("clients.form.edit.description")
            : t("clients.form.create.description")}
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Seção: Dados da Empresa */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("clients.form.sections.companyData")}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.name")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.cnpj")}
                        placeholder=" "
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          e.target.value = formatCpfCnpj(value);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="corporate_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.corporate_name")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.email")}
                        placeholder=" "
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_classification"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("clients.form.fields.business_classification")}
                        options={businessClassificationOptions}
                        value={field.value || undefined}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        placeholder={t("generic.selectAnOption")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Seção: Endereço */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("clients.form.sections.address")}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="address.postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.postal_code")}
                        placeholder=" "
                        {...field}
                        onBlur={handleAddressBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.street"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.street")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.number"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.number")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.complement"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.complement")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.neighborhood"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.neighborhood")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.city")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.state"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.state")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Seção: Representante da Empresa */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("clients.form.sections.companyRepresentative")}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="company_representative.first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.first_name")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_representative.last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.last_name")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_representative.personal_email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.personal_email")}
                        placeholder=" "
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_representative.cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.cpf")}
                        placeholder=" "
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          e.target.value = formatCpfCnpj(value);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_representative.mobile_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.mobile_phone")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Seção: Dados Bancários */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("clients.form.sections.bankData")}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="bank_account.bank_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.bank_name")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_account.bank_branch"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.bank_branch")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_account.checking_account"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.checking_account")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_account.checking_account_digit"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("clients.form.fields.checking_account_digit")}
                        placeholder=" "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="bank_account.pix_key"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("clients.form.fields.pix_key")}
                      placeholder={t("clients.form.placeholders.pix_key")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Seção: Documentos */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("clients.form.sections.documents")}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="document.company_logo"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        label={t("clients.form.fields.company_logo")}
                        description={t("clients.form.fields.company_logo_description")}
                        accept="image/*"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="document.cnpj_card"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        label={t("clients.form.fields.cnpj_card")}
                        description={t("clients.form.fields.cnpj_card_description")}
                        accept=".pdf,.jpg,.jpeg,.png"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4">
              <FormField
                control={form.control}
                name="document.signed_contract"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        label={t("clients.form.fields.signed_contract")}
                        description={t("clients.form.fields.signed_contract_description")}
                        accept=".pdf,.jpg,.jpeg,.png"
                        value={field.value as TUploadValue | undefined}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        multiple
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={form.formState.isSubmitting}
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  setIsClientFormOpen(false);
                  form.reset();
                }
              }}
            >
              {t("generic.cancel")}
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {selectedClient ? t("generic.save") : t("generic.register")}
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
};
